/**
 * Decorator for $controller service
 */

/**
 * $controller decorator <br>
 * Add ability to extend controllers
 *
 * @class ControllerDecorator
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc decorator
 * @name mwApp/common.service:ControllerDecorator
 */
/* @ngInject */
export default function ControllerDecorator ($delegate, $injector) {
  // controller name matcher
  var ctrlMatchRE = /^[A-Z]\S+Ctrl(\s+as\s+\S+)?$/

  return function (expression) {
    // instantiate controller
    var controller = $delegate.apply(null, arguments)
    var controllerInstance = controller
    var Class = controllerInstance.constructor
    var i

    // if this is not our controller - skip it
    if (typeof expression !== 'string' || !ctrlMatchRE.test(expression)) {
      return controller
    }

    // if controller was created with undocumented private api for later initialization
    // @see https://github.com/angular/angular.js/blob/master/src/ng/controller.js#L126
    /* istanbul ignore if */
    if (Class === Function && controller.instance) {
      controllerInstance = controller.instance
      Class = controllerInstance.constructor
    }

    // extend controller itself with mixes
    if (controllerInstance.mixes) {
      for (i = 0; i < controllerInstance.mixes.length; i++) {
        mixin(controllerInstance, controllerInstance.mixes[i])
      }
    }

    // extend controller's constructor prototype with mixes
    if (Class && !Class.prototype.mixed) {
      // add global mixins
      mixin(Class.prototype, 'stopEventCtrlMixin')
      mixin(Class.prototype, 'locationCtrlMixin')
      mixin(Class.prototype, 'formCtrlMixin')

      // add defined mixins
      if (Class.mixes) {
        for (i = 0; i < Class.mixes.length; i++) {
          mixin(Class.prototype, Class.mixes[i])
        }
      }

      // set flag 'mixed' to true, so we know, that we already mixed all mixins here
      Class.prototype.mixed = true
    }

    // return controller instance
    return controller
  }

  /* === private helper functions === */

  /**
   * Helper function, just check if object is empty <br>
   * this is much faster, than Object.keys(obj).length === 0
   *
   * @private
   * @memberof mwApp/common.service:ControllerDecorator
   * @param  {Object} obj - object to check
   * @return {Boolean}
   */
  function isEmptyObject (obj) {
    for (var prop in obj) {
      if (obj.hasOwnProperty(prop)) {
        return false
      }
    }
    return true
  }

  /**
   * Get mixin by function <br>
   * actually just return that function, no more
   *
   * @private
   * @memberof mwApp/common.service:ControllerDecorator
   * @param  {Function} f - mixin function
   * @return {Function}
   */
  function fromFunction (f) {
    return f
  }

  /**
   * Get mixin by name <br>
   * retrieve it from angular injector
   *
   * @private
   * @memberof mwApp/common.service:ControllerDecorator
   * @param  {String} name - mixin name
   * @return {Object} mixin
   */
  function fromString (name) {
    if ($injector.has(name)) {
      return $injector.get(name)
    }
  }

  /**
   * Get mixin from array <br>
   * first element treated as mixin name, other - as mixin parameters
   *
   * @private
   * @memberof mwApp/common.service:ControllerDecorator
   * @param  {Object[]} arr - mixin array
   * @return {Object} mixin
   */
  function fromArray (arr) {
    if (arr.length) {
      var m = getMixin(arr[0])
      if (m) {
        return arr.length === 1
          ? m
          : m.apply(null, arr.slice(1))
      }
    }
  }

  /**
   * Get mixin(s) from object <br>
   * object keys treated as mixin names, object values - as mixin parameters
   *
   * @private
   * @memberof mwApp/common.service:ControllerDecorator
   * @param  {Object} obj - mixin(s) object
   * @return {Object} mixin
   */
  function fromObject (obj) {
    var args = a => Array.isArray(a) ? a : [a]
    var ret = {}

    for (var name in obj) {
      if (obj.hasOwnProperty(name)) {
        var m = getMixin(name)
        if (m) {
          angular.extend(ret, m.apply(null, args(obj[name])))
        }
      }
    }

    return !isEmptyObject(ret) ? ret : undefined
  }

  /**
   * Get mixin by definition
   *
   * @private
   * @memberof mwApp/common.service:ControllerDecorator
   * @param  {Object} mix - mixin definition
   * @return {Object} mixin
   */
  function getMixin (mix) {
    var getters = {
      'object': fromObject,
      'array': fromArray,
      'string': fromString,
      'function': fromFunction
    }

    var type = typeof mix
    if (type === 'object' && Array.isArray(mix)) {
      type = 'array'
    }

    var getter = getters[type]
    if (getter) {
      return getter(mix)
    }
  }

  /**
   * Mix mixin to object
   *
   * @private
   * @memberof mwApp/common.service:ControllerDecorator
   * @param  {Object} obj - object to extend
   * @param  {*}      mix - mixin definition
   */
  function mixin (obj, mix) {
    var m = getMixin(mix)
    if (m) {
      if (typeof m._init === 'function') {
        m._init.call(obj)
        delete m._init
      }
      angular.extend(obj, m)
    }
  }
}
